import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@positivote/design-system/components/Button'
import { Div } from '@positivote/design-system/components/Div'
import { TextArea } from '@positivote/design-system/components/TextArea'
import { Typography } from '@positivote/design-system/components/Typography'
import { FormContainer } from '@positivote/design-system/components-form/Container'
import { FormTextField } from '@positivote/design-system/components-form/TextField'
import { CloseIcon } from '@positivote/design-system/icons/Close'
import { SendIcon } from '@positivote/design-system/icons/Send'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { TextDialog } from '@/common/components'
import { i18n } from '@/common/i18n'
import { HelpSystemRequirementeForm } from '@/modules/requirements/contracts/form'
import { useCreateHelpSystemRequirement } from '@/modules/requirements/hooks'
import { helpFormValidationSchema } from '@/modules/requirements/validations'

export interface HelpFormProps {
  result: string
  product: string | undefined
}

export function HelpForm({ product, result }: HelpFormProps): JSX.Element {
  const createHelpSystemRequirement = useCreateHelpSystemRequirement()
  const [isSuccessHelpModalOpen, setIsSuccessHelpModalOpen] = useState(false)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<HelpSystemRequirementeForm>({
    mode: 'onSubmit',
    resolver: async (values, ...args) => yupResolver(helpFormValidationSchema)(values, ...args)
  })

  function onSubmit(model: HelpSystemRequirementeForm): void {
    createHelpSystemRequirement.mutate({
      model: {
        ...model,
        product: product ?? 'hub',
        result
      },
      onSuccess: () => {
        setIsSuccessHelpModalOpen(true)
        reset({
          email: '',
          institutionName: '',
          name: '',
          obs: ''
        })
      }
    })
  }

  return (
    <Div css={{ display: 'flex', flexDirection: 'column', gap: '$md', alignItems: 'center' }}>
      <TextDialog
        css={{ maxWidth: 424 }}
        align="center"
        isOpen={isSuccessHelpModalOpen}
        onCancel={() => setIsSuccessHelpModalOpen(false)}
        title={{ label: i18n().modules.requirements.components.helpForm.textDialog.title }}
        contentTexts={[i18n().modules.requirements.components.helpForm.textDialog.contentText]}
        acceptAction={{
          handle: () => {
            setIsSuccessHelpModalOpen(false)
          },
          label: i18n().modules.requirements.components.helpForm.textDialog.acceptAction.label,
          icon: <CloseIcon size={18} />
        }}
      />
      <Div
        css={{
          display: 'flex',
          gap: '$4xs',
          flexDirection: 'column',
          width: '100%'
        }}
      >
        <Typography variant="titleMedium">
          {i18n().modules.requirements.components.helpForm.helpTitle}
        </Typography>
        <Typography variant="bodyMedium" css={{ color: '$on-surface-variant' }}>
          {i18n().modules.requirements.components.helpForm.helpSubTitle}
        </Typography>
      </Div>

      <FormContainer
        formHandleSubmit={handleSubmit}
        onSubmit={onSubmit}
        css={{
          display: 'flex',
          flexDirection: 'column',
          borderWidth: '$thin',
          borderStyle: 'solid',
          borderRadius: '$lg',
          padding: '$lg',
          gap: '$md',
          width: '100%',
          borderColor: '$outline-variant',
          alignItems: 'center'
        }}
      >
        <FormTextField
          required
          label={i18n().modules.requirements.components.helpForm.institution}
          variant="filled"
          control={control}
          name="institutionName"
          errorText={errors.institutionName?.message}
        />
        <FormTextField
          required
          label={i18n().modules.requirements.components.helpForm.name}
          variant="filled"
          control={control}
          name="name"
          errorText={errors.name?.message}
        />
        <FormTextField
          required
          label={i18n().modules.requirements.components.helpForm.email}
          variant="filled"
          control={control}
          name="email"
          errorText={errors.email?.message}
        />
        <FormTextField
          required
          label={i18n().modules.requirements.components.helpForm.obs}
          variant="filled"
          control={control}
          name="obs"
          errorText={errors.obs?.message}
          CustomInput={TextArea}
          css={{
            width: '100%',
            '& .TextField-Input': {
              resize: 'none',
              height: '112px',
              marginTop: '$md'
            }
          }}
        />
        <Button
          isLoading={createHelpSystemRequirement.isPending}
          type="submit"
          LeadingIcon={<SendIcon />}
        >
          {i18n().modules.requirements.components.helpForm.buttons.sendResults}
        </Button>
      </FormContainer>
    </Div>
  )
}
